import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { fetchMetadataForMints } from 'utils/tokens'
import store, { INITIAL_SWAP_TOKENS } from 'stores/store'
import { useEffect, useState } from 'react'
import Loading from '@components/loading/Loading'
import SwapPage from '@components/swap/SwapPage'
import { TokenMetadataItem } from 'types/token'
import Head from 'next/head'
import { withCommonNamespaces } from 'utils/common-namespaces'
import { useTranslation } from 'next-i18next'
import { determineQuoteToken } from 'utils/trade'

const set = store.getState().set

function makeTitle(
  inputTokenSymbol: string | undefined,
  outputTokenSymbol: string | undefined,
) {
  return `Swap ${
    inputTokenSymbol && inputTokenSymbol
      ? `${inputTokenSymbol} to ${outputTokenSymbol}`
      : ''
  }| Cabana`
}

function Swap({
  inputToken,
  outputToken,
}: {
  inputToken?: TokenMetadataItem
  outputToken?: TokenMetadataItem
}) {
  const { t } = useTranslation('swap')
  const router = useRouter()
  const [areTokensInitialized, setAreTokensInitialized] = useState(
    () => !(inputToken && outputToken),
  )
  const storeInputToken = store.getState().swap.inputToken
  const storeOutputToken = store.getState().swap.outputToken

  useEffect(() => {
    set((state) => {
      state.routeLoading = router.isFallback
    })
  }, [router.isFallback])

  useEffect(() => {
    if (!inputToken || !outputToken) return

    const { quoteToken, baseToken } = determineQuoteToken(
      inputToken,
      outputToken,
    )

    set((state) => {
      state.swap.inputToken = inputToken
      state.swap.outputToken = outputToken
      state.market.baseToken = baseToken
      state.market.quoteToken = quoteToken
    })
    setAreTokensInitialized(true)
  }, [inputToken, outputToken])

  const metaTitle = t('title', {
    fromToText:
      storeInputToken && storeOutputToken
        ? t('title-from-to-text', {
            inputSymbol: storeInputToken.symbol,
            outputSymbol: storeOutputToken.symbol,
          })
        : '',
  })

  if (router?.isFallback || !areTokensInitialized) {
    return (
      <div className="flex h-[calc(100vh-368px)] flex-col items-center justify-center">
        <Loading />
      </div>
    )
  }

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} />
        <meta name="twitter:title" content={metaTitle} />
      </Head>
      <SwapPage />
    </>
  )
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true, // pages will be generated on-demand
  }
}

export async function getStaticProps({
  params,
  locale,
}: {
  params: { mints?: string[] }
  locale: string
}) {
  const serverSideTranslationsCfg = await serverSideTranslations(
    locale,
    withCommonNamespaces(['chart', 'orderbook', 'swap', 'trade']),
  )

  if (!params.mints || params.mints.length === 0) {
    return {
      props: {
        ...serverSideTranslationsCfg,
        metaTitle: makeTitle(
          INITIAL_SWAP_TOKENS.inputToken.symbol,
          INITIAL_SWAP_TOKENS.outputToken.symbol,
        ),
      },
    }
  }

  if (params.mints.length > 1) {
    return {
      notFound: true,
    }
  }

  const mints = params.mints[0].split('-')
  const [inputMint, outputMint] = mints as (string | undefined)[]
  const metadata = await fetchMetadataForMints(mints.filter(Boolean))

  // match the metadata to the correct mint order from URL
  const inputToken = inputMint
    ? metadata?.find((m) => m.mint === inputMint)
    : undefined
  const outputToken = outputMint
    ? metadata?.find((m) => m.mint === outputMint)
    : undefined

  // if we can't find the metadata return 404
  if (!inputToken || !outputToken) {
    return {
      notFound: true,
    }
  }

  const metaTitle = makeTitle(inputToken.symbol, outputToken.symbol)

  return {
    props: {
      ...serverSideTranslationsCfg,
      inputToken,
      outputToken,
      metaTitle,
    },
  }
}

export default Swap
